import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Box, CircularProgress, Typography, Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import dayjs from 'dayjs'
import { useGetAiSkills } from '../../../../../api/denaliAssistant'
import SkillForm from './SkillForm'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  accordion: {
    marginBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    '&:before': {
      display: 'none'
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1, 2)
  },
  accordionDetails: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  versionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 500
  }
}))

function SkillVersionHistory ({ skillId, onCancel }) {
  const classes = useStyles()
  const [expandedVersion, setExpandedVersion] = useState(null)

  const { data: versionsData, isLoading } = useGetAiSkills({
    skillIds: [skillId],
    allVersions: true,
    includeAll: true,
    skip: 0,
    take: 1000
  })

  const versions = useMemo(() => {
    if (!versionsData?.skills) return []
    return [...versionsData.skills].sort((a, b) => b.version - a.version)
  }, [versionsData])

  const latestVersion = versions.length > 0 ? versions[0].version : null

  useEffect(() => {
    // Reset and expand latest version when skillId changes or versions load
    if (latestVersion !== null) {
      setExpandedVersion(latestVersion)
    } else {
      setExpandedVersion(null)
    }
  }, [skillId, latestVersion])

  const handleExpand = useCallback((version) => {
    setExpandedVersion(expandedVersion === version ? null : version)
  }, [expandedVersion])

  const handleSaveSuccess = useCallback((savedSkill) => {
    setExpandedVersion(savedSkill.version)
  }, [])

  if (isLoading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (versions.length === 0) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='100%'>
        <Typography color='textSecondary'>No versions found</Typography>
      </Box>
    )
  }

  return (
    <div className={classes.container}>
      {versions.map((skill) => {
        const isExpanded = expandedVersion === skill.version
        const createdDate = dayjs(skill.createdDate).format('YYYY-MM-DD HH:mm:ss')

        return (
          <Accordion
            key={`${skill.skillId}-${skill.version}`}
            expanded={isExpanded}
            onChange={() => handleExpand(skill.version)}
            className={classes.accordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <div className={classes.versionHeader}>
                <span>Version {skill.version}</span>
                <span>—</span>
                <span>{createdDate}</span>
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <SkillForm
                skill={skill}
                onCancel={() => handleExpand(skill.version)}
                onSuccess={handleSaveSuccess}
              />
            </AccordionDetails>
          </Accordion>
        )
      })}
    </div>
  )
}

SkillVersionHistory.propTypes = {
  skillId: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default SkillVersionHistory
